// src/hooks/useCriticalSave.ts - Critical save functionality for BCP form
import { useCallback, useState } from 'react';
import { FormState, RiskState, SaveStatus } from '../types/bcp';
import { updateBCP, createBCP } from '../services/api';

export interface CriticalSaveHook {
  criticalSave: (data: FormState) => Promise<any>;
  criticalSaveStatus: Pick<SaveStatus, 'isCriticalSaving' | 'lastSaved' | 'errors'>;
}

export const useCriticalSave = (
  bcpId: string | undefined,
  isEditing: boolean,
  risks: RiskState
): CriticalSaveHook => {
  const [criticalSaveStatus, setCriticalSaveStatus] = useState({
    isCriticalSaving: false,
    lastSaved: null as Date | null,
    errors: {} as { [section: string]: string | null }
  });

  // Critical save function for full form validation and save
  const criticalSave = useCallback(async (data: FormState) => {
    try {
      setCriticalSaveStatus(prev => ({ ...prev, isCriticalSaving: true }));

      const payload = {
        ...data,
        risk_factors: { selected: risks.primary_concerns },
        threat_probability: { selected: risks.location_risks },
      };

      let result;
      if (isEditing && bcpId) {
        result = await updateBCP(parseInt(bcpId), payload as any);
      } else {
        // For new BCPs, create directly
        // Backend handles duplicate business names by updating existing BCP
        result = await createBCP(payload as any);
      }

      setCriticalSaveStatus(prev => ({
        ...prev,
        isCriticalSaving: false,
        lastSaved: new Date(),
        errors: { ...prev.errors, critical: null }
      }));

      return result;

    } catch (error: any) {
      console.error('Critical save failed:', error);

      // Handle specific error messages
      const errorMessage = error.message || 'Failed to save BCP';

      setCriticalSaveStatus(prev => ({
        ...prev,
        isCriticalSaving: false,
        errors: { ...prev.errors, critical: errorMessage }
      }));
      throw error;
    }
  }, [isEditing, bcpId, risks]);

  return {
    criticalSave,
    criticalSaveStatus
  };
};
